import React, { ChangeEvent, Fragment, useEffect, useState } from "react";
import { useDispatch } from "react-redux";

import { Pencil, Trash, Users, Search } from "lucide-react";

import { useDeleteSubClient, useGetSubClients } from "../../hooks/sub_clients-cax";

import { TextInput } from "../shared/Inputs";
import { Tooltip } from "../shared/Tooltip";
import { EntitlementTableCells, EntitlementTableHeaderCells } from "../sub/EntitlementTableCells";

import { ISubClientListItem } from "../../types/services.interfaces/sub_clients-cax";

import { openModal } from "../../store/modal.slice";

export const SubClients = () => {
    const dispatch = useDispatch();

    const [filterInputValue, setFilterInputValue] = useState<string>('');
    const [filteredSubClients, setFilteredSubClients] = useState<ISubClientListItem[]>([]);

    const { data: subClients, isLoading } = useGetSubClients();

    useEffect(() => {
        if (!subClients) return;
        setFilteredSubClients(subClients);
    }, [subClients]);

    const handleFilter = (e: ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setFilterInputValue(value);
        setFilteredSubClients(subClients?.filter(subClient => {
            return subClient.name?.toLowerCase().includes(value.toLowerCase())
        }) ?? []);
    };

    return (
        <div className='sub_clients__page'>
            <p className='text-4xl text-semibold'>Sub Clients</p>
            {isLoading && (
                <div className="sub_clients__data__loading">
                    <div className='list-loader' />
                    <p className="text-medium">Loading Sub Clients...</p>
                </div>
            )}
            {!isLoading && (
                <Fragment>
                    <div className="sub_clients__filters">
                        <TextInput
                            icon={{ icon: <Search />, position: 'prefix' }}
                            placeholder="Search"
                            value={filterInputValue}
                            onChange={handleFilter}
                            disabled={!subClients?.length}
                        />
                    </div>
                    <div className="sub_clients__data">
                        {!subClients?.length && (
                            <div className="sub_clients__data__empty">
                                <p className="text-medium">No sub clients yet!</p>
                            </div>
                        )}
                        {!!subClients?.length && (
                            <Fragment>
                                {!filteredSubClients.length && (
                                    <div className="sub_clients__data__empty">
                                        <p className="text-medium">No sub clients found.</p>
                                    </div>
                                )}
                                {!!filteredSubClients.length && (
                                    <div className="sub_clients__data__table_wrapper">
                                        <table>
                                            <thead>
                                                <tr>
                                                    <th><p className="text-xs text-medium color--txt-disabled text-left">Name</p></th>
                                                    <th><p className="text-xs text-medium color--txt-disabled text-center">Cohorts</p></th>
                                                    <th><p className="text-xs text-medium color--txt-disabled text-center">Users</p></th>
                                                    <EntitlementTableHeaderCells />
                                                    <th><p className="text-xs text-medium color--txt-disabled text-center">Action</p></th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {filteredSubClients.map((subClient) => (
                                                    <SubClientRow key={subClient.id} subClient={subClient} />
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </Fragment>
                        )}
                        <div className="sub_clients__data__actions">
                            <button
                                className='btn btn--sm btn--radius-sm btn--primary'
                                onClick={() => dispatch(openModal({ name: 'MANAGE_SUB_CLIENT_MODAL', data: { subClientId: null } }))}
                            >
                                Add Sub Client
                            </button>
                        </div>
                    </div>
                </Fragment>
            )}
        </div>
    );
};

const SubClientRow = ({ subClient }: { subClient: ISubClientListItem }) => {
    const dispatch = useDispatch();

    const [isProcessing, setIsProcessing] = useState<boolean>(false);

    const { mutate: deleteSubClient } = useDeleteSubClient();

    const handleDeleteSubClient = () => {
        if (isProcessing) return;

        dispatch(openModal({
            name: 'CONFIRMATION_MODAL', data: {
                title: 'Delete Sub Client?',
                description: `Are you sure you want to delete the sub client "${subClient.name}"? All associated users and their entitlements will be removed, but user accounts will remain. Cohorts associations will also be removed along with their entitlements and entitlements of all cohort associated users, but the cohorts themselves will remain.`,
                onConfirm: () => {
                    deleteSubClient({ id: subClient.id, setIsProcessing: setIsProcessing });
                },
            },
        }));
    };

    return (
        <tr>
            <td><p className="text-sm text-medium">{subClient.name}</p></td>
            <td><p className="text-sm color--txt-muted text-center">{subClient.cohort_count}</p></td>
            <td><p className="text-sm color--txt-muted text-center">{subClient.user_count}</p></td>
            <EntitlementTableCells entitlements={subClient.entitlements} />
            <td>
                <div className='sub_clients__data__table__actions_cell'>
                    <Tooltip content="Manage sub client & entitlements">
                        <button
                            className="btn btn--icon-sm btn--radius-sm btn--secondary"
                            onClick={() => dispatch(openModal({ name: 'MANAGE_SUB_CLIENT_MODAL', data: { subClientId: subClient.id } }))}
                        >
                            <Pencil />
                        </button>
                    </Tooltip>
                    <Tooltip content="Manage sub client users">
                        <button
                            className="btn btn--icon-sm btn--radius-sm btn--secondary"
                            onClick={() => dispatch(openModal({ name: 'MANAGE_SUB_CLIENT_USERS_MODAL', data: { subClientId: subClient.id } }))}
                        >
                            <Users />
                        </button>
                    </Tooltip>
                    <Tooltip content="Delete sub client">
                        <button
                            className="btn btn--icon-sm btn--radius-sm btn--secondary"
                            onClick={handleDeleteSubClient}
                            disabled={isProcessing}
                        >
                            <Trash />
                        </button>
                    </Tooltip>
                </div>
            </td>
        </tr>
    );
};